import { Button, useDisclosure } from "@chakra-ui/react";
import {
  ClosingMenuProps,
  useClosingMenuItems,
} from "features/closings/ClosingMenu";
import { CreateClosingModal } from "features/closings/CreateClosingDialog";
import { ExistingClosingDialog } from "features/closings/ExistingClosingDialog";
import { CommitmentsTable } from "features/commitments/CommitmentsTable";
import { CommitmentsTabProps } from "features/commitments/tabs/CommitmentsTabProps";
import {
  useClosingCreate,
  useClosingDelete,
  useClosingListSuspense,
} from "gen/endpoints/closing/closing";
import { useClosingTableRetrieveSuspense } from "gen/endpoints/closing-table-data/closing-table-data";
import { ClosingCreateDataRequest } from "gen/model/closingCreateDataRequest";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import { isEmpty } from "lodash-es";
import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useOutletContext } from "react-router-dom";
import { QueryBoundary } from "shared/api/QueryBoundary";
import { Restricted } from "shared/components/access/Restricted";
import { PageTabBody } from "shared/components/layout/PageTabBody";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { GetLinkInfoForRowFn } from "shared/components/table/data/DataTableTypes";
import { MaybernTableRowMenu } from "shared/components/table/maybern/MaybernTableRowMenu";
import { useToast } from "shared/components/toast/useToast";
import { rootRoute } from "shared/navigation/Routes";
import invariant from "shared/utils/TinyInvariant";
import { useBoolean } from "usehooks-ts";

export const CommitmentsTabClosings: React.FC = () => {
  const { fundFamilyId } = useOutletContext<CommitmentsTabProps>();
  const { t } = useTranslation(["commitment", "common", "closing"]);
  const { showErrorToast } = useToast();
  const navigate = useNavigate();

  const { data: tableData, refetch: refetchClosingTable } =
    useClosingTableRetrieveSuspense(fundFamilyId);
  const { data: draftClosings, refetch: refetchClosingList } =
    useClosingListSuspense(fundFamilyId, {
      is_draft: true,
    });

  const refetchClosingInformation = async () => {
    await Promise.all([refetchClosingList(), refetchClosingTable()]);
  };
  const { mutateAsync: createFundFamilyClosing } = useClosingCreate({
    mutation: {
      onSuccess: () => refetchClosingInformation(),
    },
  });

  const fundFamilyRoute = useMemo(
    () => rootRoute({}).fundFamilies({}).fundFamily({ fundFamilyId }),
    [fundFamilyId],
  );

  const {
    isOpen: isCreateClosingModalOpen,
    onOpen: openCreateClosingModal,
    onClose: closeCreateClosingModal,
  } = useDisclosure();

  const {
    value: isExistingClosingDialogOpen,
    setTrue: openExistingClosingDialog,
    setFalse: closeExistingClosingDialog,
  } = useBoolean();

  const {
    mutateAsync: deleteFundFamilyClosing,
    isPending: isDeleteClosingLoading,
  } = useClosingDelete();

  const getLinkInfoForRow = useCallback<GetLinkInfoForRowFn>(
    ({ row }) =>
      row.data?.metadata?.type === "closing"
        ? {
            type: "generic",
            to: fundFamilyRoute
              .closing({})
              .edit({ closingId: row.data.metadata.closing_id }).$,
          }
        : undefined,
    [fundFamilyRoute],
  );

  const modalIsLoading = isDeleteClosingLoading;

  const closingId = !isEmpty(draftClosings) ? draftClosings[0].id : undefined;

  const createClosing = async (data: ClosingCreateDataRequest) => {
    try {
      const closing = await createFundFamilyClosing({
        fundFamilyId,
        data: {
          fund_family_id: fundFamilyId,
          closing_date: data.closing_date,
        },
      });
      await refetchClosingInformation();
      navigate(fundFamilyRoute.closing({}).edit({ closingId: closing.id! }).$);
    } catch (err) {
      showErrorToast(err);
    }
  };

  const navigateToDraftClosing = () =>
    navigate(fundFamilyRoute.closing({}).edit({ closingId: closingId! }).$);

  return (
    <PageTabBody>
      <TableBox>
        <TableBoxHeader>
          <TableBoxTitle>{t("closing:closingHistory")}</TableBoxTitle>
          <DataTableBoxActions>
            <Restricted permission={PermissionTypeEnum.write}>
              <Button
                colorScheme="tertiary"
                size="xs"
                onClick={() => {
                  if (isEmpty(draftClosings)) {
                    openCreateClosingModal();
                  } else {
                    openExistingClosingDialog();
                  }
                }}
              >
                {t("commitment:newClosing")}
              </Button>
            </Restricted>
          </DataTableBoxActions>
        </TableBoxHeader>
        <TableBoxBody>
          <DataTable
            data={tableData}
            getLinkInfoForRow={getLinkInfoForRow}
            emptyStateProps={{
              title: t("commitment:empty.title"),
              description: t("commitment:empty.description"),
            }}
            renderRowActions={({ row }) => {
              invariant(row.data?.metadata?.type === "closing");
              return {
                type: "buttons",
                actions: (
                  <QueryBoundary>
                    <ClosingRowMenu
                      fundFamilyId={fundFamilyId}
                      closingData={row.data.metadata}
                    />
                  </QueryBoundary>
                ),
              };
            }}
          />
        </TableBoxBody>
      </TableBox>
      <CommitmentsTable
        fundFamilyId={fundFamilyId}
        tableTitle={t("closing:draftCommitmentsOutsideClosing")}
        hideIfEmpty={true}
        params={{
          is_unattached_to_closing: true,
        }}
      />
      {isCreateClosingModalOpen && (
        <CreateClosingModal
          fundFamilyId={fundFamilyId}
          createClosing={createClosing}
          onCloseComplete={closeCreateClosingModal}
        />
      )}
      {isExistingClosingDialogOpen && (
        <ExistingClosingDialog
          isLoading={modalIsLoading}
          routeToDraftClosing={navigateToDraftClosing}
          discardAndCreateClosing={async () => {
            try {
              await deleteFundFamilyClosing({
                fundFamilyId,
                closingId: closingId!,
              });
              await refetchClosingInformation();
              openCreateClosingModal();
            } catch (e) {
              showErrorToast(e);
            }
          }}
          onCloseComplete={closeExistingClosingDialog}
        />
      )}
    </PageTabBody>
  );
};

const ClosingRowMenu: React.FC<ClosingMenuProps> = (props) => {
  const menuItems = useClosingMenuItems(props);
  if (menuItems.length === 0) {
    return null;
  }
  return <MaybernTableRowMenu>{menuItems}</MaybernTableRowMenu>;
};
